import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { flowsApi } from "../services/rag-service";

export function useFlowSummaries({
  userId,
  organizationId,
  projectIds,
  name,
  status,
  view,
  limit,
  enabled,
}: FlowSummaryQuery) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState(name.trim());
  const [navigation, setNavigation] = useState<{
    scope: string;
    cursors: string[];
  }>({ scope: "", cursors: [] });

  useEffect(() => {
    const timer = setTimeout(() => setSearch(name.trim()), 250);
    return () => clearTimeout(timer);
  }, [name]);

  const scope = JSON.stringify([
    userId,
    organizationId,
    [...projectIds].sort(),
    search,
    status,
    view,
    limit,
  ]);
  const cursors = navigation.scope === scope ? navigation.cursors : [];
  const cursor = cursors.at(-1) || "";
  const query = useQuery<FlowSummaryPage>({
    queryKey: [...FLOW_SUMMARIES_QUERY_KEY, userId, scope, cursor],
    queryFn: ({ signal }) =>
      flowsApi.getFlowSummaries({
        projectIds, name: search, status, view, limit, cursor, signal,
      }),
    enabled: enabled && Boolean(userId) && projectIds.length > 0,
    retry: (count, error) =>
      count < 1 &&
      (!isAxiosError(error) || !error.response || error.response.status >= 500),
  });

  useEffect(() => {
    if (cursor && isAxiosError(query.error) && query.error.response?.status === 409) {
      setNavigation({ scope, cursors: [] });
      void queryClient.invalidateQueries({
        queryKey: [...FLOW_SUMMARIES_QUERY_KEY, userId],
      });
    }
  }, [cursor, query.error, queryClient, scope, userId]);

  const refresh = () => {
    setNavigation({ scope, cursors: [] });
    return queryClient.invalidateQueries({
      queryKey: [...FLOW_SUMMARIES_QUERY_KEY, userId],
    });
  };
  const busy = query.isFetching || search !== name.trim();
  return {
    ...query,
    flows: query.isError || !projectIds.length ? [] : query.data?.data || [],
    busy,
    hasPrevious: cursors.length > 0,
    hasNext: !query.isError && Boolean(query.data?.next),
    previous: () => {
      if (!busy) setNavigation({ scope, cursors: cursors.slice(0, -1) });
    },
    next: () => {
      if (!busy && !query.isError && query.data?.next) {
        setNavigation({ scope, cursors: [...cursors, query.data.next] });
      }
    },
    refresh,
  };
}

export const FLOW_SUMMARIES_QUERY_KEY = ["flowSummaries"];

interface FlowSummaryQuery {
  userId?: string;
  organizationId?: string;
  projectIds: string[];
  name: string;
  status: string;
  view: string;
  limit: number;
  enabled: boolean;
}

interface FlowSummary {
  id: string;
  projectId: string;
  displayName: string;
  status: "ENABLED" | "DISABLED";
  created: string;
  updated: string;
  publishedVersionId: string | null;
  pieces: string[];
}

interface FlowSummaryPage {
  data: FlowSummary[];
  next: string | null;
}
